/* eslint-disable @typescript-eslint/no-extraneous-class */
import type { Logger } from '@feature-hub/core';
import {
  cleanupEmissionConsumptionMarkup,
  renderTextWithFootnotesReferencesV2,
  getConsumptionsAndEmissionsWithIdentifiers,
} from '../feature-service-types';
import type { VueFormatterServiceInterfaceV1, LocaleServiceV1 } from '../feature-service-types';
import type { ReactNode } from 'react';

export interface ConsumptionEmissionValue {
  consumption: ReactNode;
  emission: ReactNode;
}

export interface WltpProps {
  formattedConsumption?: Iterable<ReactNode> | string | undefined;
  formattedEmission?: Iterable<ReactNode> | undefined;
  formattedCo2Class?: Iterable<ReactNode> | undefined;
  formattedDischargedCo2Class?: Iterable<ReactNode> | undefined;
  formattedDischargedConsumption?: Iterable<ReactNode> | undefined;
}

type myCallBack = (wltp: WltpProps[]) => void;
export class VueServiceHelper {
  static getConsumptionAndEmission = async (
    wltpValues: string[] | null,
    mycallback: myCallBack,
    vueFormatterFeatureService: VueFormatterServiceInterfaceV1,
    localeService: LocaleServiceV1,
    logger?: Logger,
  ) => {
    const consumptionsAndEmissionsValues = await getConsumptionsAndEmissionsWithIdentifiers(
      wltpValues as string[],
      vueFormatterFeatureService,
      localeService,
    );

    const format = (value: string) =>
      renderTextWithFootnotesReferencesV2(cleanupEmissionConsumptionMarkup(value));

    const wltp = (wltpValues as string[]).reduce<WltpProps[]>((acc, wltpKey) => {
      const entry = consumptionsAndEmissionsValues[wltpKey];
      if (!entry) {
        logger?.warn('No consumption/emission data found for carline key.', {
          event: 'consumption-emission-data-missing',
          wltpKey,
        });
        return acc;
      }

      const {
        formattedConsumption,
        formattedEmission,
        formattedCo2Class,
        formattedDischargedCo2Class,
        formattedDischargedConsumption,
      } = entry;

      if (!formattedConsumption || !formattedEmission) {
        return acc;
      }

      const wltpData: WltpProps = {
        formattedConsumption: format(formattedConsumption),
        formattedEmission: format(formattedEmission),
        ...(formattedCo2Class && { formattedCo2Class: format(formattedCo2Class) }),
        ...(formattedDischargedCo2Class && {
          formattedDischargedCo2Class: format(formattedDischargedCo2Class),
        }),
        ...(formattedDischargedConsumption && {
          formattedDischargedConsumption: format(formattedDischargedConsumption),
        }),
      };

      acc.push(wltpData);
      return acc;
    }, []);

    mycallback(wltp);
  };

  static isAudiCode(value: string): boolean {
    const audiCodeRegExp = /^[A-Z0-9]+$/;
    return audiCodeRegExp.test(value);
  }

  static isCarLineCode(value: string): boolean {
    const carlineRegExp = /^[a-z0-9_-]+$/;
    return carlineRegExp.test(value);
  }
}
